import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout, { DateInput, SearchableSelect } from './Layout';
import { useNavigate } from 'react-router-dom';
import { formatDate, getApiUrl } from './config';

function SalesReport() {
    const role = localStorage.getItem("role") || "user";
    const isAdmin = role === "admin";
    const [sales, setSales] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [customersById, setCustomersById] = useState({});
    const [selectedVehicleId, setSelectedVehicleId] = useState('');
    const [customers, setCustomers] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [viewInvoice, setViewInvoice] = useState(null);
    const navigate = useNavigate();

    const formatWhatsAppNumber = (value) => {
        const digits = String(value || "").replace(/\D/g, "").replace(/^0+/, "");
        if (!digits) return "";
        if (digits.length === 10) return `91${digits}`;
        return digits;
    };

    const openWhatsApp = (phone, message) => {
        const digits = formatWhatsAppNumber(phone);
        if (!digits) {
            alert("Customer WhatsApp number not found");
            return;
        }
        const url = `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const formatPaymentSplit = (cash, upi, bank) => {
        const c = Number(cash) || 0;
        const u = Number(upi) || 0;
        const b = Number(bank) || 0;
        const parts = [];
        if (c > 0) parts.push(`Cash ₹ ${c.toFixed(2)}`);
        if (u > 0) parts.push(`UPI ₹ ${u.toFixed(2)}`);
        if (b > 0) parts.push(`Bank ₹ ${b.toFixed(2)}`);
        return parts.length > 0 ? parts.join(" | ") : "-";
    };

    const buildWhatsAppMessage = (row) => {
        const dateText = formatDate(row.date);
        const totalAmount = Number(row.total_amount || 0);
        const paidAmount = Number(row.paid_amount || 0);
        const cumulativeBalance = Number(row.cumulative_balance ?? row.net_balance ?? 0);
        const prevBalance = cumulativeBalance - (totalAmount - paidAmount);

        const totalText = `₹ ${totalAmount.toFixed(2)}`;
        const prevText = `₹ ${prevBalance.toFixed(2)}`;
        const cashAmt = Number(row.cash_amount || 0);
        const upiAmt = Number(row.upi_amount || 0);
        const remainingText = `₹ ${cumulativeBalance.toFixed(2)}`;
        const totalAmtText = `₹ ${(prevBalance + totalAmount).toFixed(2)}`;
        return [
            `Invoice #${row.invoice_id}`,
            `Date: ${dateText}`,
            row.items_summary || "-",
            `Net Amount: ${totalText}`,
            `Previous Amt: ${prevText}`,
            `Total Amount: ${totalAmtText}`,
            `Cash Amt: ₹ ${cashAmt.toFixed(2)}`,
            `UPI Amt: ₹ ${upiAmt.toFixed(2)}`,
            `Balance Amt: ${remainingText}`
        ].join("\n");
    };

    const buildWhatsAppMessageFromInvoice = (row, invoice) => {
        const dateText = formatDate(invoice?.date || row.date);
        const itemsLines = (invoice?.items || []).map((it, idx) => {
            const qty = Number(it.quantity || 0);
            const wt = Number(it.weight || 0);
            const rate = Number(it.price || 0);
            const discRate = Math.max(0, Number(it.discount_rate || 0));
            return [
                `${idx + 1}. ${it.product_name || "-"}`,
                `Qty: ${qty}`,
                `Weight: ${wt.toFixed(2)}`,
                `Rate: ${rate.toFixed(2)}`,
                `Discount: ${discRate.toFixed(2)}`
            ].join("\n");
        });

        const totalAmount = Number(invoice?.total_amount ?? row.total_amount ?? 0);
        const paidAmount = Number(invoice?.paid_amount ?? row.paid_amount ?? 0);
        const cumulativeBalance = Number(row.cumulative_balance ?? row.net_balance ?? 0);
        const prevBalance = cumulativeBalance - (totalAmount - paidAmount);
        const paymentsArr = Array.isArray(invoice?.payments) ? invoice.payments : [];
        const modeTotals = paymentsArr.reduce((acc, p) => {
            const key = String(p?.payment_mode || "").toLowerCase();
            const amt = Number(p?.amount || 0);
            if (!amt) return acc;
            if (key.includes("upi")) acc.upi += amt;
            else if (key.includes("bank") || key.includes("transfer")) acc.bank += amt;
            else acc.cash += amt;
            return acc;
        }, { cash: 0, upi: 0, bank: 0 });
        const cashAmt = paymentsArr.length > 0 ? Number(modeTotals.cash || 0) : Number(row.cash_amount || 0);
        const upiAmt = paymentsArr.length > 0 ? Number(modeTotals.upi || 0) : Number(row.upi_amount || 0);

        const totalText = `₹ ${totalAmount.toFixed(2)}`;
        const prevText = `₹ ${prevBalance.toFixed(2)}`;
        const remainingText = `₹ ${cumulativeBalance.toFixed(2)}`;
        const totalAmtText = `₹ ${(prevBalance + totalAmount).toFixed(2)}`;
        return [
            `Invoice #${row.invoice_id}`,
            `Date: ${dateText}`,
            ...(itemsLines.length > 0 ? itemsLines : [row.items_summary || "-"]),
            `Net Amount: ${totalText}`,
            `Previous Amt: ${prevText}`,
            `Total Amount: ${totalAmtText}`,
            `Cash Amt: ₹ ${cashAmt.toFixed(2)}`,
            `UPI Amt: ₹ ${upiAmt.toFixed(2)}`,
            `Balance Amt: ${remainingText}`
        ].join("\n");
    };

    const handleWhatsApp = async (row) => {
        const phone = getCustomerPhone(row);
        const digits = formatWhatsAppNumber(phone);
        if (!digits) {
            alert("Customer WhatsApp number not found");
            return;
        }
        try {
            const res = await axios.get(`${getApiUrl()}/sales/${row.invoice_id}`);
            const message = buildWhatsAppMessageFromInvoice(row, res.data);
            openWhatsApp(phone, message);
        } catch (err) {
            console.error(err);
            openWhatsApp(phone, buildWhatsAppMessage(row));
        }
    };

    const handleExportPdf = () => {
        window.print();
    };

    const getCustomerPhone = (row) => {
        const fromReport = row.customer_contact;
        if (fromReport !== undefined && fromReport !== null && String(fromReport).trim() !== "") return fromReport;
        return customersById[row.customer_id] || "";
    };

    const fetchVehicles = useCallback(async () => {
        try {
            const res = await axios.get(`${getApiUrl()}/vehicles`);
            setVehicles(res.data);
        } catch (err) {
            console.error(err);
        }
    }, []);

    const fetchCustomers = useCallback(async () => {
        try {
            const res = await axios.get(`${getApiUrl()}/customers`);
            const map = {};
            (res.data || []).forEach(c => {
                map[c.id] = c.contact;
            });
            setCustomersById(map);
            setCustomers(res.data || []);
        } catch (err) {
            console.error(err);
        }
    }, []);

    const fetchSales = useCallback(async () => {
        try {
            const params = {};
            if (selectedVehicleId) params.vehicle_id = selectedVehicleId;
            if (selectedCustomerId) params.customer_id = selectedCustomerId;
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;
            const res = await axios.get(`${getApiUrl()}/reports/sales`, {
                params
            });
            setSales(res.data);
        } catch (err) {
            console.error(err);
        }
    }, [selectedVehicleId, selectedCustomerId, fromDate, toDate]);

    const handleExportTally = async () => {
        try {
            const params = { type: "sales" };
            if (selectedVehicleId) params.vehicle_id = selectedVehicleId;
            if (selectedCustomerId) params.customer_id = selectedCustomerId;
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;
            const res = await axios.get(`${getApiUrl()}/exports/tally`, {
                params,
                responseType: "blob"
            });
            const disposition = res.headers && (res.headers["content-disposition"] || res.headers["Content-Disposition"]);
            const match = String(disposition || "").match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : "tally_sales.xml";
            const blobUrl = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error(err);
            alert("Failed to export Tally XML");
        }
    };

    useEffect(() => {
        fetchVehicles();
    }, [fetchVehicles]);

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    useEffect(() => {
        fetchSales();
    }, [fetchSales]);

    const handleDelete = async (id) => {
        if (!isAdmin) return alert("Only admin can delete records");
        if (window.confirm("Are you sure you want to delete this invoice? Stock will be reversed.")) {
            try {
                await axios.delete(`${getApiUrl()}/sales/${id}`);
                fetchSales();
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleView = async (id) => {
        try {
            const res = await axios.get(`${getApiUrl()}/sales/${id}`);
            setViewInvoice(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Layout>
            <div className="container mt-4">
                <h2>Sales Report</h2>
                <div className="row g-3 align-items-end mb-3 no-print">
                    <div className="col-md-3">
                        <label className="form-label fw-bold">From</label>
                        <DateInput value={fromDate} onChange={setFromDate} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label fw-bold">To</label>
                        <DateInput value={toDate} onChange={setToDate} />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label fw-bold">Vehicle No</label>
                        <select className="form-select" value={selectedVehicleId} onChange={e => setSelectedVehicleId(e.target.value)}>
                            <option value="">All Vehicles</option>
                            {vehicles.map(v => (
                                <option key={v.id} value={v.id}>{v.vehicle_no}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-4">
                        <label className="form-label fw-bold">Customer</label>
                        <SearchableSelect
                            value={selectedCustomerId}
                            options={customers.map(c => ({ value: String(c.id), label: c.name || '' }))}
                            placeholder="All customers..."
                            onChange={(nextId) => setSelectedCustomerId(nextId)}
                            allowClear
                        />
                    </div>
                    <div className="col-md-2">
                        <button type="button" className="btn btn-primary w-100" onClick={handleExportTally}>
                            Export Tally XML
                        </button>
                    </div>
                    <div className="col-md-2">
                        <button type="button" className="btn btn-outline-dark w-100" onClick={handleExportPdf}>
                            Export PDF
                        </button>
                    </div>
                </div>
                <div className="table-responsive">
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Customer</th>
                                <th>Vehicle No</th>
                                <th>Items</th>
                                <th>Qty</th>
                                <th>Weight</th>
                                <th>Rate</th>
                                <th>Invoice Total</th>
                                <th>Paid Amount</th>
                                <th>Paid Split</th>
                                <th>Receipt</th>
                                <th>Receipt Split</th>
                                <th>Net Bal</th>
                                <th className="no-print">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sales.map(s => (
                                <tr key={s.invoice_id}>
                                    <td>{formatDate(s.date)}</td>
                                    <td>{s.customer_name}</td>
                                    <td>{s.vehicle_no || '-'}</td>
                                    <td>{s.items_summary}</td>
                                    <td>{Number(s.total_qty || 0)}</td>
                                    <td>{Number(s.total_weight || 0).toFixed(2)}</td>
                                    <td>{Number(s.rate_per_kg || 0).toFixed(2)}</td>
                                    <td className="fw-bold">₹ {(s.total_amount || 0).toFixed(2)}</td>
                                    <td className="text-success">₹ {(s.paid_amount || 0).toFixed(2)}</td>
                                    <td>{formatPaymentSplit(s.cash_amount, s.upi_amount, s.bank_amount)}</td>
                                    <td className="text-success">₹ {(Number(s.receipt_amount) || 0).toFixed(2)}</td>
                                    <td>{formatPaymentSplit(s.receipt_cash_amount, s.receipt_upi_amount, s.receipt_bank_amount)}</td>
                                    <td className="text-danger fw-bold">₹ {(Number(s.cumulative_balance ?? s.net_balance) || 0).toFixed(2)}</td>
                                    <td className="no-print">
                                        <button className="btn btn-info btn-sm me-1" onClick={() => handleView(s.invoice_id)}>View</button>
                                        <button
                                            className="btn btn-success btn-sm me-1"
                                            onClick={() => handleWhatsApp(s)}
                                        >
                                            WhatsApp
                                        </button>
                                        {isAdmin && (
                                            <>
                                                <button type="button" className="btn btn-warning btn-sm me-1" onClick={() => navigate(`/sale?edit=${s.invoice_id}`)}>Edit</button>
                                                <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDelete(s.invoice_id)}>Delete</button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* View Modal */}
                {viewInvoice && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Invoice #{viewInvoice.id}</h5>
                                    <button type="button" className="btn-close" onClick={() => setViewInvoice(null)}></button>
                                </div>
                                <div className="modal-body">
                                    <p><strong>Date:</strong> {formatDate(viewInvoice.date)}</p>
                                    <p><strong>Customer ID:</strong> {viewInvoice.customer_id}</p>
                                    <p><strong>Vehicle:</strong> {viewInvoice.vehicle_no || '-'}</p>
                                    <p><strong>Paid Amount:</strong> ₹ {(Number(viewInvoice.paid_amount) || 0).toFixed(2)} ({formatPaymentSplit(viewInvoice.cash_amount, viewInvoice.upi_amount, viewInvoice.bank_amount)})</p>
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Product</th>
                                                <th>Quantity</th>
                                                <th>Price</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {viewInvoice.items.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>{item.product_name}</td>
                                                    <td>{item.quantity}</td>
                                                    <td>{item.price}</td>
                                                    <td>{item.total}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <th colSpan="3" className="text-end">Grand Total</th>
                                                <th>₹ {(viewInvoice.total_amount || 0).toFixed(2)}</th>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setViewInvoice(null)}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default SalesReport;
